import type { PitchCoordinates, PitchBounds, PitchTransform } from './types'
import { CANVAS_PADDING } from './constants'

/**
 * Lateral (Y axis) span of a pitch, derived from its raw corner coords.
 * The reporting-api returns pitches with the lateral axis in `[0, widthM]`
 * (non-centred), so any view that wants "0 = pocket centre" needs to know
 * the real `centreY` for this specific session — hardcoded sport widths
 * aren't enough because sessions can be played on any sized field.
 */
export function pitchLateralSpan(coordinates: PitchCoordinates): {
  widthM: number
  halfWidth: number
  centreY: number
} {
  const bounds = computePitchBounds(coordinates)
  const widthM = bounds.maxY - bounds.minY
  return {
    widthM,
    halfWidth: widthM / 2,
    centreY: (bounds.minY + bounds.maxY) / 2
  }
}

/**
 * Compute axis-aligned bounding box from pitch coordinates.
 */
export function computePitchBounds(coordinates: PitchCoordinates): PitchBounds {
  const points = Object.values(coordinates)
  if (points.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 }
  }

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity

  for (const p of points) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y
    if (p.y > maxY) maxY = p.y
  }

  return { minX, maxX, minY, maxY }
}

/**
 * Create an aspect-fit transform from world coordinates (meters) to canvas pixels.
 *
 * Y is flipped because pitch coordinates use math convention (Y up) while
 * canvas uses screen convention (Y down). The transform preserves aspect ratio
 * by fitting the pitch within the available canvas area (minus padding), centering
 * any remaining space.
 *
 * The returned worldToCanvas/canvasToWorld functions are used for both drawing
 * (world→canvas) and hit testing (canvas→world for coordinate display).
 */
export function createTransform(
  bounds: PitchBounds,
  canvasWidth: number,
  canvasHeight: number,
  padding = CANVAS_PADDING
): PitchTransform {
  const pitchW = bounds.maxX - bounds.minX
  const pitchH = bounds.maxY - bounds.minY

  const availW = canvasWidth - padding * 2
  const availH = canvasHeight - padding * 2

  const scale =
    pitchW > 0 && pitchH > 0 ? Math.min(availW / pitchW, availH / pitchH) : 1

  const offsetX = padding + (availW - pitchW * scale) / 2
  const offsetY = padding + (availH - pitchH * scale) / 2

  const worldToCanvas = (x: number, y: number) => ({
    cx: offsetX + (x - bounds.minX) * scale,
    cy: offsetY + (pitchH - (y - bounds.minY)) * scale // flip Y
  })

  const canvasToWorld = (cx: number, cy: number) => ({
    x: (cx - offsetX) / scale + bounds.minX,
    y: pitchH - (cy - offsetY) / scale + bounds.minY // flip Y back
  })

  return { worldToCanvas, canvasToWorld, scale, bounds }
}
